import { faCircleInfo, faClipboard } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo, useState } from "react";
import { graphql, useRefetchableFragment } from "react-relay/hooks";
import { Link } from "react-router-dom";

import { Button, Card, Heading, Paragraph, Switch, Text, toast, Tooltip } from "@skydio/rivet-ui";
import { Collapsible } from "@skydio/rivet-ui/src/incubator/collapsible/Collapsible";

import { useConnectionNodes } from "components/common/Pagination";
import { useSetOrgDefaultLoginTypes } from "hooks/mutation/setOrgDefaultLoginTypes";
import { useOverrideLink } from "hooks/orgPaths";
import { useIsAdmin } from "hooks/useIsAdmin";
import { displayMutationResults } from "utils/mutations";

import NewSsoModal from "./NewSsoModal";
import SsoMenuDropdown from "./SsoMenuDropdown";

import type { OrgLoginMethods_loginMethods$key } from "__generated__/OrgLoginMethods_loginMethods.graphql";
import type { LoginTypesUpdate } from "__generated__/setOrgDefaultLoginTypesMutation.graphql";
import type { IdentityProviderConfiguration } from "./UserProfile";

export const orgLoginMethodsFragment = graphql`
  fragment OrgLoginMethods_loginMethods on Organization
  @refetchable(queryName: "OrgLoginMethodsRefetchQuery") {
    id
    loginOverrideCounts {
      loginType
      key
      displayName
      count
    }
    identityProviderConfigurations {
      __id
      edges {
        node {
          id
          uuid
          name
          displayName
          providerType
          samlAcsUrl
          samlMetadataUrl
          oidcCallbackUrl
          oidcInitiateLoginUrl
          oidcClientId
          oidcIssuer
          oidcUsePkce
          axonConnectDomain
        }
      }
    }
    loginTypes(orgDefault: true) {
      edges {
        node {
          loginType
          authEnabled
          identityProviderConfiguration {
            id
            uuid
            name
            providerType
            samlAcsUrl
            samlMetadataUrl
            oidcCallbackUrl
            oidcInitiateLoginUrl
            oidcClientId
            oidcIssuer
            oidcUsePkce
            axonConnectDomain
          }
        }
      }
    }
  }
`;

interface MetadataRowProps {
  label: string;
  value: string;
}

const MetadataEntry = ({ label, value }: MetadataRowProps) => {
  const handleClick = useCallback(async () => {
    navigator.clipboard.writeText(value);
    toast.success(label + " copied to clipboard", { duration: 3000 });
  }, [label, value]);
  return (
    <div className="gap-y-2">
      <label className="font-semibold">{label}</label>
      <span className="flex h-7 items-stretch align-middle">
        <Text className="inline-block h-full min-w-[36rem] max-w-xl overflow-hidden truncate whitespace-nowrap border border-solid bg-gray-50 px-2 align-middle text-gray-700">
          {value}{" "}
        </Text>
        <Button variant="link" size="sm" className="py-0" isIconOnly onClick={handleClick}>
          <FontAwesomeIcon icon={faClipboard} />
        </Button>
      </span>
    </div>
  );
};

interface OrgLoginMethodsProps {
  loginMethodsRef: OrgLoginMethods_loginMethods$key;
  onUpdate?: () => void;
}

const OrgLoginMethods = ({ loginMethodsRef, onUpdate }: OrgLoginMethodsProps) => {
  const isAdmin = useIsAdmin();
  const [queryData, refetch] = useRefetchableFragment(orgLoginMethodsFragment, loginMethodsRef);
  const idpConnection = queryData?.identityProviderConfigurations?.__id ?? "";
  const overrideStats = queryData.loginOverrideCounts;
  const idpCfgs = useConnectionNodes(queryData.identityProviderConfigurations);
  const loginTypes = useConnectionNodes(queryData.loginTypes);
  const orgId = queryData.id;

  const getOverrideCount = useCallback(
    (key: string) => {
      return overrideStats?.find(s => s?.key === key)?.count || 0;
    },
    [overrideStats]
  );

  const axonIdp = useMemo(() => idpCfgs.find(idp => idp.providerType === "AXON"), [idpCfgs]);
  const [axonEnabled, setAxonEnabled] = useState(
    loginTypes.some(t => t.authEnabled && t.loginType === "AXON")
  );

  const [emailSet, setEmailSet] = useState(
    loginTypes.some(t => t.authEnabled && t.loginType === "ONE_TIME_PASSWORD")
  );
  const [ssoSet, setSsoSet] = useState(
    loginTypes.find(t => t.authEnabled && t.loginType === "SSO")?.identityProviderConfiguration
      ?.uuid ?? null
  );
  const [modified, setModified] = useState(false);

  const [updateLoginTypes] = useSetOrgDefaultLoginTypes(
    displayMutationResults(response => response.setOrgDefaultLoginTypes, {
      onSuccess: () => {
        setModified(false);
        refetch({}, { fetchPolicy: "network-only" });
        onUpdate?.();
      },
      successMessage: () => `Successfully updated default login methods`,
    })
  );
  const baseOverrideLink = useOverrideLink("");

  const [pendingUpdate, setPendingUpdate] = useState<LoginTypesUpdate[]>([]);

  const handleEmailUpdate = useCallback(
    (enabled: boolean) => {
      const existingLoginUpdate = pendingUpdate.find(t => t.loginType === "ONE_TIME_PASSWORD");
      if (existingLoginUpdate) {
        console.log("existing update found");
        const newUpdate: LoginTypesUpdate = {
          ...existingLoginUpdate,
          authEnabled: enabled,
        };
        setPendingUpdate(p => p.map(t => (t.loginType === "ONE_TIME_PASSWORD" ? newUpdate : t)));
      } else {
        setPendingUpdate(p => [...p, { authEnabled: emailSet, loginType: "ONE_TIME_PASSWORD" }]);
      }
      setEmailSet(enabled);
      setModified(true);
    },
    [emailSet, pendingUpdate]
  );

  const handleSsoUpdate = useCallback(
    (enabled: boolean, idp: IdentityProviderConfiguration) => {
      const newSsoSetting = enabled ? idp.uuid : null;
      setSsoSet(newSsoSetting);
      const existingLoginUpdate = pendingUpdate.find(t => t.loginType === "SSO");
      if (existingLoginUpdate) {
        const newUpdate: LoginTypesUpdate = {
          ...existingLoginUpdate,
          authEnabled: enabled,
          identityProviderConfigurationId: newSsoSetting,
        };
        setPendingUpdate(p => p.map(t => (t.loginType === "SSO" ? newUpdate : t)));
      } else {
        const newUpdate: LoginTypesUpdate = {
          authEnabled: enabled,
          loginType: "SSO",
          identityProviderConfigurationId: newSsoSetting,
        };
        setPendingUpdate(p => [...p, newUpdate]);
      }
      setModified(true);
    },
    [pendingUpdate]
  );

  const handleAxonUpdate = useCallback(
    (enabled: boolean, idp?: IdentityProviderConfiguration) => {
      if (!idp) {
        // if no axon idp, do nothing. This shouldn't happen in UI
        return;
      }
      setAxonEnabled(enabled);
      const existingLoginUpdate = pendingUpdate.find(t => t.loginType === "AXON");
      // Update an existing update if it exists, otherwise add a new one
      if (existingLoginUpdate) {
        const newUpdate: LoginTypesUpdate = {
          ...existingLoginUpdate,
          authEnabled: enabled,
          identityProviderConfigurationId: enabled ? idp.uuid : null,
        };
        setPendingUpdate(p => p.map(t => (t.loginType === "AXON" ? newUpdate : t)));
      } else {
        // Update existing update
        setPendingUpdate(p => [
          ...p,
          {
            authEnabled: enabled,
            loginType: "AXON",
            identityProviderConfigurationId: enabled ? idp.uuid : null,
          },
        ]);
      }
      setModified(true);
    },
    [pendingUpdate]
  );

  const handleAxonSwitch = useCallback(
    (enabled: boolean) => {
      handleAxonUpdate(enabled, axonIdp);
    },
    [axonIdp, handleAxonUpdate]
  );

  const handleSave = useCallback(() => {
    updateLoginTypes(orgId, pendingUpdate);
    setPendingUpdate([]);
  }, [orgId, pendingUpdate, updateLoginTypes]);

  return (
    <div className="space-y-3">
      <div className="flex w-full flex-col align-middle">
        <div className="flex items-center gap-2">
          <Paragraph emphasis="secondary">Manage login methods for all users below.</Paragraph>
          <a
            href="https://support.skydio.com/hc/en-us/articles/33456277678235-How-to-configure-SSO-in-Skydio-Cloud"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700"
          >
            Learn more
          </a>
        </div>
      </div>

      <Card className="space-y-0">
        <Card.Header className="pb-2">
          <Heading level={5}>Email Passcode</Heading>
        </Card.Header>
        <Card.Body className="pt-2">
          <Card>
            <Card.Body className="flex justify-between">
              <div className="flex items-center justify-between">
                <div>
                  <Heading level={6}>Enabled by default</Heading>
                  <Paragraph emphasis="secondary">All users can sign in via email</Paragraph>
                </div>
                <div className="flex items-center gap-2">
                  {/* TODO once we support disabling email logins, update this to not be disabled*/}
                  <Tooltip
                    showArrow={true}
                    color="default"
                    closeDelay={350}
                    content="Disabling one-time password logins is currently disabled"
                  >
                    <div>
                      <FontAwesomeIcon size="lg" icon={faCircleInfo} />
                    </div>
                  </Tooltip>
                  <Switch
                    // TODO (david.taylor): remove true once we support disabling email logins
                    disabled={true || !isAdmin}
                    size="large"
                    checked={emailSet}
                    onCheckedChange={handleEmailUpdate}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
          <Card.Footer className="justify-end p-2">
            <Paragraph>
              Number of overrides:{" "}
              <Link to={baseOverrideLink + "Email"} target="e_blank" rel="noopener noreferrer">
                {overrideStats?.find(s => s?.key === "Email")?.count || 0} Users
              </Link>
            </Paragraph>
          </Card.Footer>
        </Card.Body>
      </Card>
      {axonIdp !== undefined && (
        <Card key={axonIdp.uuid} className="space-y-0">
          <Card.Header className="pb-2">
            <Heading level={5}>Axon Connect</Heading>
            <SsoMenuDropdown
              identityProvider={axonIdp}
              overrideCount={getOverrideCount(axonIdp.uuid)}
              enabled={axonEnabled}
              connectionId={idpConnection}
              orgNodeId={orgId}
            />
          </Card.Header>
          <Card.Body className="pt-2">
            <Card>
              <Card.Body className="flex justify-between">
                <div className="flex items-center justify-between">
                  <div>
                    <Heading level={6}>Enabled by default</Heading>
                    <Paragraph emphasis="secondary">
                      All users can sign in via Axon Connect
                    </Paragraph>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      size="large"
                      disabled={!isAdmin}
                      checked={axonEnabled}
                      onCheckedChange={handleAxonSwitch}
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
            <Card.Footer className="justify-between py-2 pl-0 pr-2">
              <Collapsible
                className="w-max min-w-[41rem] items-end justify-end"
                header="Provider Information"
                titleWeight="normal"
              >
                <Card>
                  <Card.Body>
                    <div className="items-start justify-items-stretch gap-2">
                      <MetadataEntry label="Axon Domain" value={axonIdp.axonConnectDomain ?? ""} />
                    </div>
                  </Card.Body>
                </Card>
              </Collapsible>
              <Paragraph>
                Number of overrides:{" "}
                <Link
                  to={baseOverrideLink + axonIdp.uuid}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {getOverrideCount(axonIdp.uuid)} Users
                </Link>
              </Paragraph>
            </Card.Footer>
          </Card.Body>
        </Card>
      )}
      {idpCfgs
        .filter(idp => idp.providerType !== "AXON")
        .map(idp => {
          return (
            <Card key={idp.uuid} className="space-y-0">
              <Card.Header className="justify-between pb-2">
                <Heading level={5}>{`${idp.name} (${idp.providerType})`}</Heading>
                <SsoMenuDropdown
                  identityProvider={idp}
                  overrideCount={getOverrideCount(idp.uuid)}
                  enabled={idp.uuid === ssoSet}
                  connectionId={idpConnection}
                  orgNodeId={orgId}
                />
              </Card.Header>
              <Card.Body className="pt-2">
                <Card>
                  <Card.Body className="flex justify-between">
                    <div className="flex items-center justify-between">
                      <div>
                        <Heading level={6}>Enabled by default</Heading>
                        <Paragraph emphasis="secondary">
                          All users can sign in via {idp.name}
                        </Paragraph>
                      </div>
                      <div>
                        <Switch
                          size="large"
                          disabled={!isAdmin || (idp.uuid !== ssoSet && ssoSet !== null)}
                          checked={idp.uuid === ssoSet}
                          onCheckedChange={enabled => {
                            handleSsoUpdate(enabled, idp);
                          }}
                        ></Switch>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
                <Card.Footer className="justify-between py-2 pl-0 pr-2">
                  <Collapsible
                    className="w-max min-w-[41rem] items-end justify-end"
                    header="Provider Information"
                    titleWeight="normal"
                  >
                    <Card>
                      <Card.Body>
                        <div className="items-start justify-items-stretch gap-2 space-y-2">
                          {idp.samlMetadataUrl && (
                            <MetadataEntry
                              label="Entity ID/Metadata URL"
                              value={idp.samlMetadataUrl}
                            />
                          )}
                          {idp.samlAcsUrl && (
                            <MetadataEntry label="ACS URL" value={idp.samlAcsUrl} />
                          )}
                          {idp.oidcCallbackUrl && (
                            <MetadataEntry label="Callback URL" value={idp.oidcCallbackUrl} />
                          )}
                          {idp.oidcInitiateLoginUrl && (
                            <MetadataEntry
                              label="Initiate Login URL"
                              value={idp.oidcInitiateLoginUrl}
                            />
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Collapsible>
                  <Paragraph>
                    Number of overrides:{" "}
                    <Link
                      to={baseOverrideLink + idp.uuid}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {getOverrideCount(idp.uuid)} Users
                    </Link>
                  </Paragraph>
                </Card.Footer>
              </Card.Body>
            </Card>
          );
        })}
      {isAdmin && (
        <div>
          <NewSsoModal
            orgNodeId={orgId}
            connection={idpConnection}
            axonExists={axonIdp !== undefined}
          />
        </div>
      )}

      <Button isDisabled={!modified || !isAdmin} onClick={handleSave} variant="primary" size="lg">
        Save
      </Button>
    </div>
  );
};

export default OrgLoginMethods;
